/**
 * The URL a share is sent as: `/:account/kelly-ai/artifacts/:resourceId`. A segment under Kelly's route
 * rather than one of its own — `/kelly-ai` is already a deep link to a view state (ADR 0020).
 */

import { useEffect, useMemo, useRef } from "react"
import { useAppSelector } from "hooks/redux"
import { selectArtifactDeepLink } from "../store/agent-chat-slice"
import type { FileArtifact } from "./artifact-contract"
import { useSharedArtifacts } from "./artifact-sharing"

export type ArtifactDeepLink =
  /** Not on the deep-link route at all. */
  | { status: "inactive" }
  | { status: "loading", resourceId: string }
  | { status: "found", resourceId: string, artifact: FileArtifact, name: string }
  | { status: "not-found", resourceId: string }

/**
 * Resolved from the artifacts list, already filtered server-side, so "no grant", "revoked", "wrong account"
 * and "never existed" are one answer. A lookup of its own could have told them apart.
 */
export function useArtifactDeepLink(): ArtifactDeepLink {
  // From the store, not the route — see `pendingArtifactDeepLink`.
  const resourceId = useAppSelector(selectArtifactDeepLink)

  const { artifacts, isLoading, isError } = useSharedArtifacts(resourceId != null)

  return useMemo<ArtifactDeepLink>(() => {
    if (resourceId == null) return { status: "inactive" }
    // An errored list is not evidence it is gone, but there is nothing else to say.
    if (isLoading) return { status: "loading", resourceId }

    const row = artifacts.find(artifact => artifact.id === resourceId)
    if (row == null || isError) return { status: "not-found", resourceId }

    return {
      status: "found",
      resourceId,
      name: row.name,
      // `id` stays the source artifact id so events keep naming one thing; `resourceId` routes the fetches.
      artifact: { id: row.sourceArtifactId, type: "file", filename: row.filename, contentType: row.contentType, resourceId: row.id },
    }
  }, [resourceId, artifacts, isLoading, isError])
}

interface ArtifactDeepLinkOpenerProps {
  /** Called once per resource id, with the artifact to show full screen. */
  onOpen: (artifact: FileArtifact, name: string) => void
}

/**
 * Renders nothing; mounted only while a link is pending. The lookup needs a QueryClient and the account
 * alias, which the panel's other hosts — the app-level dock, a dozen stories and tests — do not provide.
 */
export function ArtifactDeepLinkOpener({ onOpen }: ArtifactDeepLinkOpenerProps) {
  const deepLink = useArtifactDeepLink()
  // Once per resource id: the link outlives the close, and re-opening would make Close inert.
  const handledRef = useRef<string | null>(null)

  useEffect(() => {
    if (deepLink.status === "inactive" || deepLink.status === "loading") return
    if (handledRef.current === deepLink.resourceId) return
    handledRef.current = deepLink.resourceId
    if (deepLink.status === "found") onOpen(deepLink.artifact, deepLink.name)
  }, [deepLink, onOpen])

  return null
}
